import { useAtom, useSetAtom } from 'jotai';
import { useMemo } from 'react';

import {
  defaultEnrollmentFilters,
  enrollmentAppliedFiltersAtom,
  enrollmentFiltersAtom,
  hasAnyEnrollmentFilter,
} from '../store/atoms/enrollmentFiltersAtom';
import type { EnrollmentFilters } from '../types/enrollment';

import { useEnrollmentGrid } from './useEnrollmentGrid';

export interface EnrollmentStats {
  readonly totalCount: number;
  readonly classesCount: number;
  readonly coursesCount: number;
  readonly polosCount: number;
}

const DEFAULT_PAGE_SIZE = 50;

/**
 * Lista de matrículas agrupadas por turma/polo, vinda do backend.
 * Só dispara busca quando o usuário aplica filtros via `useEnrollmentFilters().applyFilters()`.
 *
 * @param page - Página (1-based), default 1
 * @param pageSize - Itens por página, default 50
 */
export function useEnrollmentList(page = 1, pageSize = DEFAULT_PAGE_SIZE) {
  const { parents, isLoading, isError, refetch, hasSearched, totalCount, totalPages } =
    useEnrollmentGrid(page, pageSize);

  const stats = useMemo<EnrollmentStats>(() => {
    const statsCount = parents.reduce((sum, p) => sum + (p.studentsNumber ?? 0), 0);
    const classesCount = parents.length;
    const coursesCount = new Set(parents.map(p => p.courseId)).size;
    const polosCount = new Set(parents.map(p => p.poloId ?? 0)).size;
    return { totalCount: statsCount, classesCount, coursesCount, polosCount };
  }, [parents]);

  return {
    parents,
    isLoading,
    isError,
    refetch,
    hasSearched,
    totalParents: parents.length,
    totalCount,
    totalPages,
    pageSize,
    stats,
  } as const;
}

/**
 * Hook de gerenciamento dos filtros do grid de matrículas.
 *
 * - `filters`: rascunho atualizado em tempo real pelos inputs.
 * - `updateFilters`: aplica patch parcial ao rascunho.
 * - `applyFilters`: copia o rascunho para o estado "aplicado" — só aqui a busca dispara.
 * - `resetFilters`: limpa rascunho e aplicado (cancela a busca em andamento).
 * - `canApply`: indica se há algum filtro preenchido no rascunho.
 */
export function useEnrollmentFilters() {
  const [filters, setFilters] = useAtom(enrollmentFiltersAtom);
  const setApplied = useSetAtom(enrollmentAppliedFiltersAtom);

  const updateFilters = (partial: Partial<EnrollmentFilters>) => {
    setFilters(prev => ({ ...prev, ...partial }));
  };

  const applyFilters = () => {
    setApplied(filters);
  };

  const resetFilters = () => {
    setFilters(defaultEnrollmentFilters);
    setApplied(null);
  };

  const canApply = hasAnyEnrollmentFilter(filters);

  return { filters, updateFilters, applyFilters, resetFilters, canApply } as const;
}
